/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Box, Alert,
    Typography, Switch, FormControlLabel,
    Grid, Chip, MenuItem, Autocomplete, CircularProgress, IconButton,
    FormControl, FormHelperText, InputAdornment, InputLabel, OutlinedInput
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { consultarCNPJ, consultarCEP, cadastrarEmpresa, atualizarEmpresa, atualizarEmpresaViaCNPJa, listarGruposEconomicos, vincularEmpresaAoGrupo, desvincularEmpresaDoGrupo } from '../../services/api-empresas.service';
import { getHierarquia, getResponsaveisComerciais } from '../../services/api-associados-parceiros.service';
import BlueButton from '../BlueButton';
import ModalConfirmacao from '../ModalConfirmacao';
import Loading from '../Loading';

// Função para formatar CNPJ manualmente
const formatarCNPJ = (value) => {
    const cnpj = value.replace(/\D/g, '');
    if (cnpj.length <= 2) return cnpj;
    if (cnpj.length <= 5) return `${cnpj.slice(0, 2)}.${cnpj.slice(2)}`;
    if (cnpj.length <= 8) return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5)}`;
    if (cnpj.length <= 12) return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8)}`;
    return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12, 14)}`;
};

// Função para formatar CEP manualmente
const formatarCEP = (value) => {
    const cep = value.replace(/\D/g, '');
    if (cep.length <= 5) return cep;
    return `${cep.slice(0, 5)}-${cep.slice(5, 8)}`;
};

const ApiBadge = () => (
    <Typography
        component="span"
        sx={{
            color: '#2e7d32',
            fontSize: '1rem',
            ml: 0.5,
            verticalAlign: 'middle',
            cursor: 'help'
        }}
        title="Dado obtido automaticamente da Receita Federal"
    >
        *
    </Typography>
);

const SectionTitle = ({ children }) => (
    <Typography variant="subtitle2" color="primary" sx={{ mt: 2, mb: 1, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
        {children}
    </Typography>
);

const ModalCadastroEmpresa = ({ open, onClose, onSuccess, initialData, onShowNotification }) => {
    // Chave estável: evita resetar o form quando o pai re-renderiza e passa novo objeto initialData (mesma empresa)
    const initialEmpresaKey =
        initialData == null
            ? 'novo'
            : (initialData.id_empresa ?? initialData.id) != null
                ? String(initialData.id_empresa ?? initialData.id)
                : 'prefill';

    // Modo edição só se tiver ID. Se vier initialData sem ID, é "Criação Pré-preenchida"
    const isEditMode = !!initialData && !!(initialData.id || initialData.id_empresa);
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false); // Modal de confirmação

    const [cnpjInput, setCnpjInput] = useState('');

    // Estado do formulário
    const [form, setForm] = useState({});
    const [isMatriz, setIsMatriz] = useState(false);

    // Estado do vínculo Associado/Parceiro (TXM-266) + Responsável Comercial (RN-02)
    const [vinculoOpcoes, setVinculoOpcoes] = useState([]);
    const [responsaveisOpcoes, setResponsaveisOpcoes] = useState([]);
    const [loadingVinculo, setLoadingVinculo] = useState(false);
    const [vinculoSelecionado, setVinculoSelecionado] = useState(null);
    const [responsavelComercialSelecionado, setResponsavelComercialSelecionado] = useState(null);

    // Estado para Grupos Econômicos
    const [grupos, setGrupos] = useState([]);
    const [selectedGrupo, setSelectedGrupo] = useState(null);
    const [grupoInputValue, setGrupoInputValue] = useState('');
    const [loadingGrupos, setLoadingGrupos] = useState(false);
    const userChangedGrupo = React.useRef(false);

    // Estado para consulta de CEP
    const [loadingCEP, setLoadingCEP] = useState(false);

    // Estado para controlar erros de validação
    const [camposComErro, setCamposComErro] = useState({});

    // Certificado digital (opcional): extensões .pfx, .p12, .cer, .crt
    const EXTENSOES_CERTIFICADO = ['.pfx', '.p12', '.cer', '.crt'];
    const [certFile, setCertFile] = useState(null);
    const [certFileError, setCertFileError] = useState('');
    const [certFileName, setCertFileName] = useState('');
    const [certificadoRemovido, setCertificadoRemovido] = useState(false); // usuário clicou em Remover e ainda não salvou
    const fileInputRef = useRef(null);

    // Efeito para carregar usuários e grupos
    useEffect(() => {
        if (open && activeStep === 1) {
            carregarVinculoOpcoes();
            carregarGrupos();
        }
    }, [open, activeStep]);

    // Efeito para carregar dados iniciais quando modal abre ou troca empresa — NÃO depende de initialData por referência
    useEffect(() => {
        if (open) {
            // Limpa erros de validação e certificado ao abrir o modal
            setCamposComErro({});
            setCertFile(null);
            setCertFileError('');

            if (initialData) {
                //Modo Edição - normaliza dt_abertura (API pode retornar data_abertura ou dt_abertura)
                setForm({
                    ...initialData,
                    dt_abertura: initialData.dt_abertura || initialData.data_abertura || ''
                });
                setCnpjInput(initialData.cnpj || '');

                // Certificado: exibir "Certificado já cadastrado" quando a lista indicar tem_certificado ou quando vier certificado (base64)
                const temCertificado = initialData.tem_certificado === true || !!(initialData.certificado);
                setCertFileName(temCertificado ? 'Certificado já cadastrado' : '');
                setCertificadoRemovido(false);

                // Lógica Híbrida: Prioriza is_matriz, se nulo, checa controlador (legado), senão default false
                const isMatrizCalc = initialData.is_matriz ?? (initialData.controlador ?? false);
                setIsMatriz(isMatrizCalc);

                // Reset do grupo e usuários; pré-seleção será feita quando os dados carregarem
                setSelectedGrupo(null);
                setGrupoInputValue('');
                setVinculoSelecionado(null);
                setResponsavelComercialSelecionado(null);

                setActiveStep(1); // Pula busca CNPJ
            } else {
                // Modo Criação - só reseta quando modal abre, não quando grupos muda
                resetState();
            }
            userChangedGrupo.current = false;
        }
    }, [open, initialEmpresaKey]);

    const idGrupoEconomicoInicial = initialData?.id_grupo_economico;

    // Efeito separado para pré-selecionar grupo em modo edição quando grupos carregam
    useEffect(() => {
        if (open && idGrupoEconomicoInicial && grupos.length > 0 && !selectedGrupo && !userChangedGrupo.current) {
            const grp = grupos.find(g => g.idGrupoEconomico === idGrupoEconomicoInicial);
            if (grp) {
                setSelectedGrupo(grp);
                setGrupoInputValue(grp.nome || '');
            }
        }
    }, [open, idGrupoEconomicoInicial, grupos, selectedGrupo]);

    const carregarGrupos = useCallback(async (searchTerm = '') => {
        setLoadingGrupos(true);
        try {
            const filters = searchTerm.trim() ? { search: searchTerm.trim() } : {};
            const res = await listarGruposEconomicos(filters);
            if (res && res.gruposEconomicos) {
                // Deduplication logic: Keep the first occurrence of each ID
                const seen = new Set();
                const uniqueGrupos = res.gruposEconomicos.filter(item => {
                    const duplicate = seen.has(item.idGrupoEconomico);
                    seen.add(item.idGrupoEconomico);
                    return !duplicate;
                });
                setGrupos(uniqueGrupos);
            }
        } catch (error) {
            console.error('Erro ao carregar grupos', error);
        } finally {
            setLoadingGrupos(false);
        }
    }, []);

    // Debounced search effect for grupos - skip when group is already selected
    useEffect(() => {
        // If a group is selected and input matches its name, don't search
        if (selectedGrupo && grupoInputValue === selectedGrupo.nome) {
            return;
        }
        const timeoutId = setTimeout(() => {
            carregarGrupos(grupoInputValue);
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [grupoInputValue, carregarGrupos, selectedGrupo]);

    /** Monta a árvore Associado/Parceiro (TXM-266) e a lista de Responsáveis Comerciais; pré-seleciona em modo edição. */
    const carregarVinculoOpcoes = async () => {
        setLoadingVinculo(true);
        try {
            const [hierarquia, responsaveis] = await Promise.all([
                getHierarquia().then((res) => res?.data || []),
                getResponsaveisComerciais({ status: 'ativo' }).then((res) => res?.data || []),
            ]);

            const opcoes = [];
            (hierarquia || []).forEach((associado) => {
                opcoes.push({
                    value: `associado-${associado.id}`,
                    label: associado.razao_social,
                    group: associado.razao_social,
                    tipo: 'ASSOCIADO',
                    id: associado.id,
                    tipoAssociado: associado.tipo,
                    responsavelHerdadoId: associado.responsavel?.id ?? null,
                    responsavelHerdadoNome: associado.responsavel?.nome ?? null,
                });
                (associado.parceiros || []).forEach((parceiro) => {
                    opcoes.push({
                        value: `parceiro-${parceiro.id}`,
                        label: `${associado.razao_social} / ${parceiro.razao_social}`,
                        group: associado.razao_social,
                        tipo: 'PARCEIRO',
                        id: parceiro.id,
                        tipoAssociado: associado.tipo,
                        responsavelHerdadoId: parceiro.responsavel?.id ?? null,
                        responsavelHerdadoNome: parceiro.responsavel?.nome ?? null,
                    });
                });
            });
            setVinculoOpcoes(opcoes);

            const respOpcoes = (responsaveis || []).map((r) => ({ value: String(r.id), label: r.nome, id: r.id }));
            setResponsaveisOpcoes(respOpcoes);

            if (initialData) {
                const valorVinculo = initialData.id_parceiro
                    ? `parceiro-${initialData.id_parceiro}`
                    : initialData.id_associado
                        ? `associado-${initialData.id_associado}`
                        : null;
                if (valorVinculo) {
                    const opcaoEncontrada = opcoes.find((o) => o.value === valorVinculo);
                    if (opcaoEncontrada) setVinculoSelecionado(opcaoEncontrada);
                }
                const idResponsavelInicial = initialData?.Responsavel?.idUsuario;
                if (idResponsavelInicial) {
                    const respEncontrado = respOpcoes.find((r) => r.id === idResponsavelInicial);
                    setResponsavelComercialSelecionado(
                        respEncontrado || { value: String(idResponsavelInicial), label: initialData.Responsavel.nome, id: idResponsavelInicial }
                    );
                }
            }
        } catch (error) {
            console.error('Erro ao carregar Associados/Parceiros:', error);
            onShowNotification && onShowNotification('Erro ao carregar lista de Associados/Parceiros', 'error');
        } finally {
            setLoadingVinculo(false);
        }
    };

    // Função para consultar CEP automaticamente
    const handleConsultarCEP = async (cep) => {
        const cepLimpo = cep.replace(/\D/g, '');

        // Se o CEP foi apagado, limpa todos os campos de endereço
        if (cepLimpo.length === 0) {
            setForm(prev => ({
                ...prev,
                cep: '',
                logradouro: '',
                numero: '',
                complemento: '',
                bairro: '',
                municipio: '',
                uf: '',
                pais: ''
            }));
            return;
        }

        // Só consulta se tiver 8 dígitos
        if (cepLimpo.length !== 8) return;

        setLoadingCEP(true);
        try {
            const dadosCEP = await consultarCEP(cepLimpo);

            // Preenche automaticamente os campos de endereço
            setForm(prev => ({
                ...prev,
                cep: dadosCEP.cep,
                logradouro: dadosCEP.logradouro || prev.logradouro,
                bairro: dadosCEP.bairro || prev.bairro,
                municipio: dadosCEP.municipio || prev.municipio,
                uf: dadosCEP.uf || prev.uf
            }));

            // Não mostra notificação para não poluir a UX
        } catch (error) {
            // Apenas mostra erro se for relevante (CEP não encontrado é comum)
            if (error.message && !error.message.includes('não encontrado')) {
                console.error('Erro ao consultar CEP:', error);
            }
        } finally {
            setLoadingCEP(false);
        }
    };

    // useEffect para detectar quando CEP está completo e fazer consulta automática
    useEffect(() => {
        const cepLimpo = (form.cep || '').replace(/\D/g, '');

        // Se o CEP foi apagado, chama a função para limpar os campos
        if (cepLimpo.length === 0) {
            handleConsultarCEP('');
        } else if (cepLimpo.length === 8) {
            // Se o CEP está completo, consulta
            handleConsultarCEP(cepLimpo);
        }
    }, [form.cep]); // Dispara quando CEP muda


    const handleConsultar = async () => {
        setLoading(true);
        try {
            const data = await consultarCNPJ(cnpjInput);

            // Popula o form com dados da API
            setForm({
                ...data,
                dt_abertura: data.data_abertura || '',
                simples_dt_opcao: '',
                simples_dt_exclusao: '',
                // Manuais (defaults)
                inscricao_estadual: '',
                inscricao_municipal: '',
                nire: '',
                regime_tributario: 'Real',
                // legado: nunca enviar "" (quebra INTEGER no Postgres); vínculo usa id_associado/id_parceiro
                id_usuario_parceiro: null,
                status: 'ATIVO',
                observacoes: ''
            });

            setActiveStep(1);
        } catch (error) {
            // Exibir mensagem específica do backend
            const mensagemErro = error.response?.data?.erro || error.message || 'Erro ao consultar CNPJ';

            // Adicionar dica se for CNPJ inválido
            let mensagemFinal = mensagemErro;
            if (mensagemErro.toLowerCase().includes('inválido')) {
                mensagemFinal = `${mensagemErro}. Verifique se o CNPJ foi digitado corretamente.`;
            } else if (mensagemErro.toLowerCase().includes('não encontrado')) {
                mensagemFinal = `${mensagemErro}. Este CNPJ pode estar inativo ou não existir.`;
            }

            onShowNotification && onShowNotification(mensagemFinal, 'error');
        } finally {
            setLoading(false);
        }
    };

    const [confirmUpdateCnpjOpen, setConfirmUpdateCnpjOpen] = useState(false);

    const handleAtualizarViaCNPJa = async () => {
        // Em modo edição, precisa de ID da empresa
        if (isEditMode) {
            const empresaId = initialData?.id_empresa || initialData?.id;
            if (!empresaId) {
                onShowNotification && onShowNotification('Esta operação só está disponível para empresas já cadastradas', 'warning');
                return;
            }
        } else {
            // Em modo cadastro, precisa ter CNPJ
            if (!form.cnpj) {
                onShowNotification && onShowNotification('CNPJ não disponível para consulta', 'warning');
                return;
            }
        }
        setConfirmUpdateCnpjOpen(true);
    };

    const executarAtualizacaoCNPJa = async () => {
        setConfirmUpdateCnpjOpen(false);
        setLoading(true);
        try {
            let response;

            if (isEditMode) {
                // Modo Edição: Atualiza via ID
                const empresaId = initialData?.id_empresa || initialData?.id;
                response = await atualizarEmpresaViaCNPJa(empresaId);

                // Atualiza o formulário com os dados retornados da API
                if (response && response.empresa) {
                    setForm(prev => ({ ...prev, ...response.empresa }));
                }
            } else {
                // Modo Cadastro: Consulta via CNPJ
                const cnpjLimpo = form.cnpj.replace(/\D/g, '');
                const data = await consultarCNPJ(cnpjLimpo);

                // Atualiza o formulário mantendo campos manuais já preenchidos
                setForm(prev => ({
                    ...data,
                    // Preserva campos que o usuário pode ter preenchido manualmente
                    inscricao_estadual: prev.inscricao_estadual || '',
                    inscricao_municipal: prev.inscricao_municipal || '',
                    nire: prev.nire || '',
                    regime_tributario: prev.regime_tributario || 'Real',
                    id_usuario_parceiro: prev.id_usuario_parceiro || null,
                    status: prev.status || 'ATIVO',
                    observacoes: prev.observacoes || '',
                    dt_abertura: prev.dt_abertura || prev.data_abertura || data.data_abertura || '',
                    simples_dt_opcao: prev.simples_dt_opcao || '',
                    simples_dt_exclusao: prev.simples_dt_exclusao || ''
                }));
            }

            onShowNotification && onShowNotification('✅ Dados atualizados com sucesso via CNPJá!', 'success');
        } catch (error) {
            const mensagemErro = error.response?.data?.erro || error.message || 'Erro ao atualizar via CNPJá';
            onShowNotification && onShowNotification('❌ ' + mensagemErro, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    // Abre modal de confirmação
    const handleSalvar = () => {
        // Limpa erros anteriores
        setCamposComErro({});

        // Objeto para armazenar campos com erro
        const erros = {};

        // Validação de campos obrigatórios
        if (!form.email || form.email.trim() === '') {
            erros.email = true;
        }

        if (!form.numero || form.numero.trim() === '') {
            erros.numero = true;
        }

        // Se houver erros, marca os campos e exibe mensagem
        if (Object.keys(erros).length > 0) {
            setCamposComErro(erros);
            const camposFaltantes = [];
            if (erros.email) camposFaltantes.push('E-mail');
            if (erros.numero) camposFaltantes.push('Número');
            onShowNotification && onShowNotification(
                `Os seguintes campos são obrigatórios: ${camposFaltantes.join(', ')}`,
                'error'
            );
            return;
        }

        setConfirmModalOpen(true);
    };

    // Executa o salvamento após confirmação
    const handleConfirmarSalvar = async () => {
        if (certFileError) return;
        setLoading(true);
        try {
            const payload = {
                ...form,
                data_abertura: form.dt_abertura || form.data_abertura || null,
                simples_dt_opcao: form.simples_dt_opcao || null,
                simples_dt_exclusao: form.simples_dt_exclusao || null,
                is_matriz: isMatriz,
                id_grupo_economico: selectedGrupo?.idGrupoEconomico || null,
                id_associado: vinculoSelecionado?.tipo === 'ASSOCIADO' ? vinculoSelecionado.id : null,
                id_parceiro: vinculoSelecionado?.tipo === 'PARCEIRO' ? vinculoSelecionado.id : null,
                id_usuario_responsavel_comercial: ehVinculoInterno && responsavelComercialSelecionado ? responsavelComercialSelecionado.id : null,
                // "" quebra INTEGER no Postgres (coluna antiga id_usuario_parceiro)
                id_usuario_parceiro: form.id_usuario_parceiro || null,
                ...(isEditMode && certificadoRemovido && { removerCertificado: true })
            };

            let responseEmpresa;
            if (certFile) {
                const formData = new FormData();
                Object.keys(payload).forEach(key => {
                    const value = payload[key];
                    if (value !== undefined && value !== null && value !== '') {
                        formData.append(key, typeof value === 'object' && value instanceof Date ? value.toISOString() : value);
                    }
                });
                formData.append('certFile', certFile);
                if (isEditMode) {
                    const empresaId = initialData.id_empresa || initialData.id;
                    responseEmpresa = await atualizarEmpresa(empresaId, formData);
                    onShowNotification && onShowNotification('Empresa atualizada com sucesso!', 'success');
                } else {
                    responseEmpresa = await cadastrarEmpresa(formData);
                    onShowNotification && onShowNotification('Empresa cadastrada com sucesso!', 'success');
                }
            } else {
                if (isEditMode) {
                    const empresaId = initialData.id_empresa || initialData.id;
                    responseEmpresa = await atualizarEmpresa(empresaId, payload);
                    onShowNotification && onShowNotification('Empresa atualizada com sucesso!', 'success');
                } else {
                    responseEmpresa = await cadastrarEmpresa(payload);
                    onShowNotification && onShowNotification('Empresa cadastrada com sucesso!', 'success');
                }
            }

            if (onSuccess) onSuccess();
            onClose();
        } catch (error) {
            const mensagemErro = 'Erro ao salvar: ' + (error.response?.data?.erro || error.message);
            onShowNotification && onShowNotification(mensagemErro, 'error');
        } finally {
            setLoading(false);
        }
    };

    const validarExtensaoCertificado = (fileName) => {
        if (!fileName) return true;
        const ext = fileName.slice(fileName.lastIndexOf('.')).toLowerCase();
        return EXTENSOES_CERTIFICADO.some(e => e.toLowerCase() === ext);
    };

    const handleCertificadoChange = (event) => {
        const file = event.target.files?.[0];
        setCertFileError('');
        if (!file) {
            setCertFile(null);
            setCertFileName('');
            return;
        }
        if (!validarExtensaoCertificado(file.name)) {
            setCertFileError('Aceito apenas .pfx, .p12, .cer ou .crt');
            setCertFile(null);
            setCertFileName('');
            event.target.value = '';
            return;
        }
        setCertFile(file);
        setCertFileName(file.name);
        setCertificadoRemovido(false);
    };

    const removerCertificado = () => {
        setCertFile(null);
        setCertFileName('');
        setCertFileError('');
        setCertificadoRemovido(true); // ao salvar, API deve limpar o certificado
    };

    const resetState = () => {
        setActiveStep(0);
        setCnpjInput('');
        setForm({});
        setIsMatriz(false);
        setSelectedGrupo(null);
        setGrupoInputValue('');
        setCertFile(null);
        setCertFileName('');
        setCertFileError('');
        setCertificadoRemovido(false);
        setVinculoSelecionado(null);
        setResponsavelComercialSelecionado(null);
    };

    // RN-02: só o vínculo com Taxation (Equipe Interna) libera escolha livre do Responsável
    // Comercial; qualquer outro Associado/Parceiro já vem com o responsável herdado, bloqueado.
    const ehVinculoInterno = vinculoSelecionado?.tipoAssociado === 'INTERNO';
    const responsavelDisabled = !vinculoSelecionado || !ehVinculoInterno;
    const responsavelOptions = ehVinculoInterno
        ? responsaveisOpcoes
        : (vinculoSelecionado?.responsavelHerdadoId
            ? [{ value: String(vinculoSelecionado.responsavelHerdadoId), label: vinculoSelecionado.responsavelHerdadoNome || 'Responsável do associado', id: vinculoSelecionado.responsavelHerdadoId }]
            : responsaveisOpcoes);
    const responsavelEfetivo = ehVinculoInterno
        ? responsavelComercialSelecionado
        : responsavelOptions.find((o) => o.id === vinculoSelecionado?.responsavelHerdadoId) ?? null;
    const responsavelHelperText = !vinculoSelecionado
        ? null
        : (!ehVinculoInterno ? 'Campo automático - Definido pelo Associado/Parceiro selecionado' : null);

    return (
        <>
            <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ bgcolor: '#0d47a1', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        {isEditMode
                            ? `Editar Empresa: ${form.razao_social || '...'}`
                            : (activeStep === 0 ? 'Nova Empresa - Consulta CNPJ' : `Cadastro: ${form.razao_social || ''}`)
                        }
                    </Typography>
                    {(isEditMode || activeStep === 1) && (
                        <Button
                            variant="contained"
                            onClick={handleAtualizarViaCNPJa}
                            disabled={loading}
                            sx={{
                                bgcolor: '#4CAF50',
                                '&:hover': { bgcolor: '#45a049' },
                                color: 'white',
                                fontSize: '0.875rem',
                                textTransform: 'none',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            🔄 Atualizar via CNPJá
                        </Button>
                    )}
                </DialogTitle>

                <DialogContent dividers>
                    {loading && <Loading loading={loading} overlay={true} />}
                    {activeStep === 0 && !isEditMode ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="body1" gutterBottom>Digite o CNPJ para buscar os dados automaticamente na Receita Federal.</Typography>
                            <TextField
                                label="CNPJ"
                                value={cnpjInput}
                                onChange={(e) => setCnpjInput(formatarCNPJ(e.target.value))}
                                fullWidth
                                autoFocus
                                sx={{ maxWidth: 400, mt: 2 }}
                                placeholder="00.000.000/0000-00"
                                inputProps={{ maxLength: 18 }}
                            />
                        </Box>
                    ) : (
                        /* Passo 2: Formulário Detalhado */
                        <Box>
                            {!isEditMode && (
                                <Alert severity="info" icon={false} sx={{ mb: 2 }}>
                                    Dados carregados via <strong>CNPJá</strong>. Verifique e complemente as informações manuais.
                                </Alert>
                            )}

                            <Grid container spacing={2}>
                                {/* DADOS PRINCIPAIS */}
                                <Grid item xs={12}><SectionTitle>🏢 Dados Principais</SectionTitle></Grid>

                                <Grid item xs={12} sm={3}>
                                    <TextField label="CNPJ" value={form.cnpj || ''} fullWidth disabled size="small" InputProps={{ endAdornment: <ApiBadge /> }} />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField label="Situação Cadastral" value={form.situacao_cadastral || ''} fullWidth disabled size="small" InputProps={{ endAdornment: <ApiBadge /> }} />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField label="Data Situação" value={form.data_situacao_cadastral || ''} fullWidth disabled size="small" InputProps={{ endAdornment: <ApiBadge /> }} />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField label="Razão Social" value={form.razao_social || ''} fullWidth size="small" disabled InputProps={{ endAdornment: <ApiBadge /> }} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField label="Nome Fantasia" value={form.nome_fantasia || ''} onChange={e => handleChange('nome_fantasia', e.target.value)} fullWidth size="small" InputProps={{ endAdornment: <ApiBadge /> }} />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        id="grupo-economico-autocomplete"
                                        options={grupos}
                                        getOptionLabel={(option) => option?.nome || ''}
                                        isOptionEqualToValue={(option, value) => option?.idGrupoEconomico === value?.idGrupoEconomico}
                                        filterOptions={(x) => x}
                                        value={selectedGrupo}
                                        onChange={(event, newValue) => {
                                            userChangedGrupo.current = true;
                                            setSelectedGrupo(newValue);
                                        }}
                                        inputValue={grupoInputValue}
                                        onInputChange={(event, newInputValue, reason) => {
                                            // 'input' = user typing, 'reset' = option selected, 'clear' = cleared
                                            setGrupoInputValue(newInputValue);
                                        }}
                                        noOptionsText={loadingGrupos ? "Buscando..." : "Nenhum grupo encontrado"}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.idGrupoEconomico}>
                                                {option.nome}
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Grupo Econômico"
                                                size="small"
                                                placeholder="Digite para pesquisar..."
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {loadingGrupos ? <CircularProgress color="inherit" size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <input
                                        ref={fileInputRef}
                                        accept=".pfx,.p12,.cer,.crt"
                                        style={{ display: 'none' }}
                                        type="file"
                                        onChange={handleCertificadoChange}
                                    />
                                    <Box
                                        onClick={() => fileInputRef.current?.click()}
                                        sx={{ cursor: 'pointer', width: '100%' }}
                                    >
                                        <TextField
                                            label="Certificado Digital"
                                            value={certFileName || ''}
                                            fullWidth
                                            size="small"
                                            placeholder=".pfx, .p12, .cer ou .crt"
                                            error={!!certFileError}
                                            helperText={certFileError}
                                            InputProps={{
                                                readOnly: true,
                                                sx: { cursor: 'pointer' },
                                                endAdornment: (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                        <IconButton
                                                            component="span"
                                                            size="small"
                                                            color="primary"
                                                            title={certFileName ? 'Trocar arquivo' : 'Selecionar arquivo'}
                                                            sx={{ p: 0.5 }}
                                                        >
                                                            <UploadFileIcon fontSize="small" />
                                                        </IconButton>
                                                        {certFileName && (
                                                            <Button
                                                                size="small"
                                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); removerCertificado(); }}
                                                                color="error"
                                                                sx={{ minWidth: 'auto', px: 1 }}
                                                            >
                                                                Remover
                                                            </Button>
                                                        )}
                                                    </Box>
                                                ),
                                            }}
                                        />
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        label="Data Abertura"
                                        type="date"
                                        value={form.dt_abertura || form.data_abertura ? String(form.dt_abertura || form.data_abertura).substring(0, 10) : ''}
                                        fullWidth
                                        size="small"
                                        disabled
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{ endAdornment: <ApiBadge /> }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField label="Natureza Jurídica" value={form.natureza_juridica || ''} fullWidth disabled size="small" InputProps={{ endAdornment: <ApiBadge /> }} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField label="Porte" value={form.porte || ''} fullWidth disabled size="small" InputProps={{ endAdornment: <ApiBadge /> }} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField label="Capital Social" value={form.capital_social || ''} fullWidth disabled size="small" InputProps={{ endAdornment: <ApiBadge /> }} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField label="CNAE Principal" value={form.cnae_principal || ''} fullWidth disabled size="small" InputProps={{ endAdornment: <ApiBadge /> }} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Data Opção Simples Nacional"
                                        type="date"
                                        value={form.simples_dt_opcao ? String(form.simples_dt_opcao).substring(0, 10) : ''}
                                        onChange={e => handleChange('simples_dt_opcao', e.target.value || '')}
                                        fullWidth
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Data Exclusão Simples Nacional"
                                        type="date"
                                        value={form.simples_dt_exclusao ? String(form.simples_dt_exclusao).substring(0, 10) : ''}
                                        onChange={e => handleChange('simples_dt_exclusao', e.target.value || '')}
                                        fullWidth
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                {/* ENDEREÇO */}
                                <Grid item xs={12}><SectionTitle>📍 Endereço</SectionTitle></Grid>

                                <Grid item xs={12} sm={2}>
                                    <TextField
                                        label="CEP"
                                        value={form.cep || ''}
                                        onChange={e => handleChange('cep', formatarCEP(e.target.value))}
                                        fullWidth
                                        size="small"
                                        placeholder="00000-000"
                                        inputProps={{ maxLength: 9 }}
                                        InputProps={{
                                            endAdornment: (
                                                <>
                                                    {loadingCEP && <CircularProgress size={16} sx={{ mr: 1 }} />}
                                                    <ApiBadge />
                                                </>
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                    <TextField label="Logradouro" value={form.logradouro || ''} fullWidth size="small" disabled InputProps={{ endAdornment: <ApiBadge /> }} />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <TextField
                                        label="Número"
                                        value={form.numero || ''}
                                        fullWidth
                                        size="small"
                                        onChange={(e) => {
                                            handleChange('numero', e.target.value);
                                            if (camposComErro.numero) {
                                                setCamposComErro(prev => ({ ...prev, numero: false }));
                                            }
                                        }}
                                        required
                                        error={camposComErro.numero}
                                        helperText={camposComErro.numero ? 'Campo obrigatório' : ''}
                                        InputProps={{ endAdornment: <ApiBadge /> }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField label="Complemento" value={form.complemento || ''} onChange={e => handleChange('complemento', e.target.value)} fullWidth size="small" InputProps={{ endAdornment: <ApiBadge /> }} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField label="Bairro" value={form.bairro || ''} fullWidth size="small" disabled InputProps={{ endAdornment: <ApiBadge /> }} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField label="Município" value={form.municipio || ''} fullWidth size="small" disabled InputProps={{ endAdornment: <ApiBadge /> }} />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <TextField label="UF" value={form.uf || ''} fullWidth size="small" disabled InputProps={{ endAdornment: <ApiBadge /> }} />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <TextField label="País" value={form.pais || ''} disabled fullWidth size="small" InputProps={{ endAdornment: <ApiBadge /> }} />
                                </Grid>

                                {/* CONTATO */}
                                <Grid item xs={12}><SectionTitle>📞 Contato</SectionTitle></Grid>

                                <Grid item xs={12} sm={4}>
                                    <TextField label="Telefone 1" value={form.telefone1 || ''} onChange={e => handleChange('telefone1', e.target.value)} fullWidth size="small" InputProps={{ endAdornment: <ApiBadge /> }} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField label="Telefone 2" value={form.telefone2 || ''} onChange={e => handleChange('telefone2', e.target.value)} fullWidth size="small" placeholder="Manual" />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        error={!!camposComErro.email}
                                    >
                                        <InputLabel shrink htmlFor="empresa-contato-email-input" required>
                                            E-mail
                                        </InputLabel>
                                        <OutlinedInput
                                            id="empresa-contato-email-input"
                                            notched
                                            label="E-mail"
                                            type="text"
                                            value={form.email != null ? String(form.email) : ''}
                                            onChange={(e) => {
                                                handleChange('email', e.target.value);
                                                if (camposComErro.email) {
                                                    setCamposComErro(prev => ({ ...prev, email: false }));
                                                }
                                            }}
                                            endAdornment={(
                                                <InputAdornment position="end">
                                                    <ApiBadge />
                                                </InputAdornment>
                                            )}
                                            inputProps={{
                                                autoComplete: 'off',
                                                spellCheck: false,
                                                maxLength: 320,
                                            }}
                                        />
                                        {camposComErro.email ? (
                                            <FormHelperText>Campo obrigatório</FormHelperText>
                                        ) : null}
                                    </FormControl>
                                </Grid>

                                {/* INSCRIÇÕES */}
                                <Grid item xs={12}><SectionTitle>📝 Inscrições</SectionTitle></Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField label="Inscrição Estadual" value={form.inscricao_estadual || ''} onChange={e => handleChange('inscricao_estadual', e.target.value)} fullWidth size="small" />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField label="Inscrição Municipal" value={form.inscricao_municipal || ''} onChange={e => handleChange('inscricao_municipal', e.target.value)} fullWidth size="small" />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField label="NIRE" value={form.nire || ''} onChange={e => handleChange('nire', e.target.value)} fullWidth size="small" />
                                </Grid>

                                {/* CONFIGURAÇÕES DO SISTEMA */}
                                <Grid item xs={12}><SectionTitle>⚙️ Configurações do Sistema</SectionTitle></Grid>

                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, border: '1px solid', borderColor: '#e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
                                        <Typography variant="body2" color="text.secondary">É uma Matriz?</Typography>
                                        <Switch
                                            checked={isMatriz}
                                            onChange={(e) => setIsMatriz(e.target.checked)}
                                            size="small"
                                        />
                                        <Typography variant="body2" color="primary" fontWeight={600}>
                                            {isMatriz ? 'Sim, é Matriz' : 'Não, é Filial'}
                                        </Typography>
                                    </Box>
                                </Grid>
                                {isMatriz && (
                                    <Grid item xs={12}>
                                        <Alert severity="warning">
                                            Esta empresa será cadastrada como <strong>cabeça de grupo econômico</strong>. Filiais podem ser vinculadas posteriormente.
                                        </Alert>
                                    </Grid>
                                )}

                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        id="vinculo-associado-parceiro-autocomplete"
                                        options={vinculoOpcoes}
                                        groupBy={(option) => option.group}
                                        getOptionLabel={(option) => option?.label || ''}
                                        isOptionEqualToValue={(a, b) => a.value === b.value}
                                        value={vinculoSelecionado}
                                        onChange={(event, newValue) => {
                                            setVinculoSelecionado(newValue);
                                            setResponsavelComercialSelecionado(null);
                                        }}
                                        noOptionsText={loadingVinculo ? 'Carregando...' : 'Nenhum associado/parceiro encontrado'}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Associado/Parceiro"
                                                required
                                                size="small"
                                                placeholder="Selecione o associado ou parceiro..."
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {loadingVinculo ? <CircularProgress color="inherit" size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        id="responsavel-comercial-autocomplete"
                                        disabled={responsavelDisabled}
                                        options={responsavelOptions}
                                        getOptionLabel={(option) => option?.label || ''}
                                        isOptionEqualToValue={(a, b) => a.value === b.value}
                                        value={responsavelEfetivo}
                                        onChange={(event, newValue) => setResponsavelComercialSelecionado(newValue)}
                                        noOptionsText="Nenhum responsável encontrado"
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Responsável Comercial"
                                                required
                                                size="small"
                                                placeholder={!vinculoSelecionado ? 'Selecione o Associado/Parceiro primeiro...' : 'Selecione o responsável...'}
                                                helperText={responsavelHelperText}
                                                InputProps={{ ...params.InputProps }}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        select
                                        label="Regime Tributário"
                                        required
                                        fullWidth
                                        size="small"
                                        value={form.regime_tributario || ''}
                                        onChange={(e) => handleChange('regime_tributario', e.target.value)}
                                    >
                                        <MenuItem value="Lucro Real">Lucro Real</MenuItem>
                                        <MenuItem value="Lucro Presumido">Lucro Presumido</MenuItem>
                                        <MenuItem value="Simples Nacional">Simples Nacional</MenuItem>
                                        <MenuItem value="MEI">MEI</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        select
                                        label="Status no Sistema"
                                        required
                                        fullWidth
                                        size="small"
                                        value={form.ativo === false ? 'inativo' : 'ativo'}
                                        onChange={(e) => handleChange('ativo', e.target.value === 'ativo')}
                                    >
                                        <MenuItem value="ativo">Ativo</MenuItem>
                                        <MenuItem value="inativo">Inativo</MenuItem>
                                    </TextField>
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField label="Observações" value={form.observacoes || ''} onChange={e => handleChange('observacoes', e.target.value)} fullWidth multiline rows={2} size="small" />
                                </Grid>

                            </Grid>
                        </Box>
                    )}

                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={onClose} color="error">Cancelar</Button>
                    {/* Passo 0 do Mode Criação */}
                    {activeStep === 0 && !isEditMode && (
                        <BlueButton textValue={loading ? "Buscando..." : "Consultar CNPJ"} clickFunction={handleConsultar} disabled={loading || cnpjInput.length < 18} />
                    )}
                    {/* Passo 1 ou Edição */}
                    {activeStep === 1 && (
                        <BlueButton textValue={loading ? "Salvando..." : (isEditMode ? "Atualizar Empresa" : "Salvar Empresa")} clickFunction={handleSalvar} disabled={loading} />
                    )}
                </DialogActions>
            </Dialog>

            <ModalConfirmacao
                open={confirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                onConfirm={handleConfirmarSalvar}
                title={isEditMode ? "Confirmar Atualização" : "Confirmar Cadastro"}
                message={
                    isEditMode
                        ? `Você tem certeza que deseja atualizar os dados da empresa "${form.razao_social}"?`
                        : `Você tem certeza que deseja cadastrar a empresa "${form.razao_social}" no sistema?`
                }
                confirmText={isEditMode ? "Sim, Atualizar" : "Sim, Cadastrar"}
                cancelText="Cancelar"
                severity="warning"
            >
                <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>CNPJ:</strong> {form.cnpj}
                    </Typography>
                </Box>
            </ModalConfirmacao>

            <ModalConfirmacao
                open={confirmUpdateCnpjOpen}
                onClose={() => setConfirmUpdateCnpjOpen(false)}
                onConfirm={executarAtualizacaoCNPJa}
                title="Atualizar via CNPJá"
                message="Deseja atualizar os dados desta empresa consultando a API CNPJá? "
                subMessage="Isso sobrescreverá os dados atuais com as informações mais recentes da Receita Federal."
                confirmText="Sim, Atualizar"
                cancelText="Cancelar"
                severity="info"
            />
        </>
    );
};

export default ModalCadastroEmpresa;
